import React from "react";
import { useTrans } from "@/utils/useTrans";
import { ProductSlug } from "@/api/product-api";

export interface ProductFeature {
  image: string;
  video?: string;
  header?: string;
  description: React.ReactNode;
}

/**
 * `<FeatureCards features={...} />` 에 넘길 제품별 카드 데이터.
 *
 * 원래는 각 소개 페이지(`src/pages/tactsuit/**`)에 로컬 상수로 박혀 있어서 구매
 * 상세페이지(PDP)에서 재사용할 수 없었다(#5068). 소개 페이지와 PDP 가 같은 소스를
 * 보도록 여기로 끌어올린다.
 *
 * ⚠️ 맵이 아니라 switch 인 이유: 번역 키는 코드에 `t("리터럴")` 로 남아 있어야
 * `/cleanup`·i18n-checker 의 grep 이 키를 인식한다. 키를 배열/상수로 빼서 `t(key)`
 * 로 넘기면 미사용 키로 오탐된다(docs/i18n.md §번역 키는 코드에 리터럴로 남겨야 한다).
 */
export const useProductFeatures = (slug: string): ProductFeature[] | null => {
  const { t } = useTrans(["tactsuit"]);

  switch (slug) {
    case ProductSlug.TACTVISOR:
      return [
        {
          image: "/images/tactsuit/tactvisor/feature1.jpg",
          header: t("The One and Only"),
          description: t(
            'Attach TactVisor to your VR HMD and experience a new level of immersion by directly "feeling" the virtual world that you have only been "seeing" before.',
          ),
        },
        {
          image: "/images/tactsuit/tactvisor/feature2.jpg",
          header: t("Hassle-free Gaming"),
          description: (
            <>
              <p className="mb-4">
                {t(
                  "TactVisor's ergonomic design made from water and pollutant-resistant material guarantees a pleasant and comfortable gaming experience, even after hours of use.",
                )}
              </p>
              <p className="text-coolgray-50">
                {t(
                  "*Compatible with Meta Quest Series, Valve Index, and Meta Rift S",
                )}
              </p>
            </>
          ),
        },
        {
          image: "/images/tactsuit/tactvisor/feature3.jpg",
          header: t("Lag-Free Feedback"),
          description: (
            <p className="max-w-[500px]">
              {t(
                "With four individually adjustable haptic feedback points on the face, you can now experience the impact of gunshots and punches in real-time and zero-delay with unparalleled realism.",
              )}
            </p>
          ),
        },
      ];

    case ProductSlug.TACTOSY_FOR_FEET:
      return [
        {
          image: "/images/tactsuit/tactosy-for-feet/feature1.jpg",
          header: t("Train like a pro"),
          description: t(
            "Feel the excitement of kicks. Bring competitive edge in playing Taekwondo, Soccer, Kickboxing in VR!",
          ),
        },
        {
          image: "/images/tactsuit/tactosy-for-feet/feature2.jpg",
          header: t("6 Feedback Points"),
          description: t(
            "Real-time haptic feedback delivered by 3 feedback points on the top of each foot will immensely improve your training experience.",
          ),
        },
        {
          image: "/images/tactsuit/tactosy-for-feet/feature3.jpg",
          header: t("Secure with Dual Locks"),
          description: t(
            "Dive into your game and feel every move and action. Real-time sound-based stereo haptic feedback will tell you the direction of gun fires, explosions and everything else happening in the game.",
          ),
        },
      ];

    default:
      return null;
  }
};
